import React from 'react'
import {useRouter} from 'next/router'

import {useAsync} from '@lib/hooks'
import {sendEmail, getIp} from '@lib/services'
import {acceptConsent} from '@lib/utils/consent'
import {useRecaptcha} from '@lib/recaptcha'
import {logEvent, useUTMContext} from '@lib/gtm'

import {IconTH} from '@components/Icon'
import {MarketingCheckbox, PrivacyNotice} from '@components/forms'
import {validateEmail} from '@lib/helpers'

type Input = {value: string}

export const ContactForm = () => {
  const router = useRouter()
  const [accept, setAccept] = React.useState(false)
  const {run: runSendEmail, isLoading, isSuccess} = useAsync(sendEmail)
  const {run: runGetIp} = useAsync(getIp)
  const {isRecaptchaValidated} = useRecaptcha()
  const ref = React.useRef<HTMLFormElement>()

  const {data: utm} = useUTMContext()

  React.useEffect(() => {
    if (isSuccess && ref.current) {
      ref.current.reset()
      setAccept(false)

      logEvent({
        ga: {
          category: 'Submit Form Success',
          action: 'Submit Form Success',
          label: router.pathname.replace('/', '').replaceAll('-', ' '),
        },
        fb: {
          event: 'Lead',
        },
      })
    }
  }, [isSuccess])

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault()

    const target = e.target as typeof e.target & {
      name: Input
      email: Input
      phone: Input
      company: Input
      accept: {checked: boolean}
    }

    const {name, email, phone, company, accept} = target

    const ip = await await runGetIp({})

    const emailData = {
      to: process.env.SALES_EMAIL,
      subject: `[AI Readiness - ${window.location.host}] ${email.value} wants to contact with you`,
      message: `
        Name: ${name.value}
        Email: ${email.value}
        Tel: (+66) ${phone.value}
        Company: ${company.value}
        IP Address: ${ip}
      `,
    }

    const reqBody = {
      name: name.value,
      email: email.value,
      phone: phone.value,
      company: company.value,
      url: window.location.href,
      marketing_consent: accept?.checked,
    }

    const res = await runSendEmail({
      emailData: {...emailData, path: process.env.EMAIL_API_URL},
      reqBody: reqBody,
      utm,
    })

    if (res.status === 200 && accept.checked) {
      const [firstName, ...rest] = name.value.split(' ')
      const lastName = rest.join(' ') || null

      await acceptConsent({
        apiKey: process.env.CONSENT_WOW_CONTACT_API_KEY,
        purposeId: process.env.CONSENT_WOW_MARKETING_CONSENT_PURPOSE_ID,
        data: {
          ...reqBody,
          name: firstName,
          surname: lastName,
          phone_number: phone.value,
        },
      })
    }
  }

  return (
    <div className='AIReadiness__contact' id='contact'>
      <div className='AIReadiness__contact__header'>
        <p>
          <svg width='12' height='12' viewBox='0 0 10 10' fill='none'>
            <path
              d='M1.4.6a.8.8 0 00-.8.8v1.2c0 .22.18.4.4.4h8a.4.4 0 00.4-.4V1.4a.8.8 0 00-.8-.8H1.4zM1 3.8a.4.4 0 00-.4.4v1.6c0 .22.18.4.4.4h8a.4.4 0 00.4-.4V4.2a.4.4 0 00-.4-.4H1zM1 7a.4.4 0 00-.4.4v1.2a.8.8 0 00.8.8h7.2a.8.8 0 00.8-.8V7.4A.4.4 0 009 7H1z'
              fill='currentColor'
            />
          </svg>
          กรอกฟอร์มได้ที่นี่ สงสัยสอบถามโทร{' '}
          <a href='tel:02-024-5560'>02-024-5560</a>
        </p>
        <h2>แบบฟอร์มเพื่อรับการประเมิน</h2>
      </div>

      <form ref={ref} className='AIReadiness__form' onSubmit={handleSubmit}>
        <div className='required'>
          <input
            name='name'
            placeholder='ชื่อ - นามสกุล'
            className='Input Input--required'
            required
          />
        </div>

        <div className='required'>
          <input
            type='email'
            name='email'
            placeholder='อีเมล'
            className='Input Input--required'
            onBlur={validateEmail}
            required
          />
        </div>

        <div className='input-phone required'>
          <IconTH />
          <span>+66</span>
          <input
            type='tel'
            name='phone'
            className='Input'
            pattern='\d{10}'
            title='Please enter a 10-digit phone number'
            required
          />
        </div>
        <div className='required'>
          <input
            name='company'
            placeholder='บริษัท'
            className='Input'
            required
          />
        </div>

        <MarketingCheckbox
          name='accept'
          className='AIReadiness__form__checkbox'
          isChecked={accept}
          onClick={() => {
            setAccept((prev) => !prev)
          }}
        />

        <button type='submit' disabled={isLoading || !isRecaptchaValidated}>
          <span>
            {isSuccess
              ? 'ดำเนินการเรียบร้อย'
              : isLoading
              ? 'กำลังดำเนินการ...'
              : !isRecaptchaValidated
              ? 'กรุณารอสักครู่...'
              : 'ส่งเเบบฟอร์ม'}
          </span>
        </button>

        <PrivacyNotice
          name='privacy-notice'
          className='AIReadiness__form__text'
        />
      </form>
    </div>
  )
}
